import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { BlotterTableStorageContext, type BlotterTableFilter, type ExtraGridState } from '@talos/kyoko';
import type { GridState } from 'ag-grid-community';
import { useCallback, useMemo, type ContextType, type PropsWithChildren } from 'react';
import { getBlottersSliceActions } from './BlottersSlice';
import { selectFullAppConfig } from './selectors';

/** Redux-based column state storage for useBlotterTable */
export const BlotterTableStorageProvider = ({ children }: PropsWithChildren) => {
  const blotterStateMap = useAppStateSelector(state => selectFullAppConfig(state).blotters3);
  const dispatch = useAppStateDispatch();

  // Stateful callback so implementers are notified when state updates and they need to re-fetch
  const getState = useCallback((blotterID: string) => blotterStateMap[blotterID], [blotterStateMap]);

  const setGridState = useCallback(
    (blotterID: string, gridState: GridState | undefined) => {
      dispatch(getBlottersSliceActions().setGridState({ blotterID, gridState }));
    },
    [dispatch]
  );

  const setFilterState = useCallback(
    (blotterID: string, filter: BlotterTableFilter | undefined) => {
      dispatch(getBlottersSliceActions().setFilterState({ blotterID, filter }));
    },
    [dispatch]
  );

  const setExtraGridState = useCallback(
    (blotterID: string, extraGridState: ExtraGridState | undefined) => {
      dispatch(getBlottersSliceActions().setExtraGridState({ blotterID, extraGridState }));
    },
    [dispatch]
  );

  const cloneState = useCallback(
    (fromBlotterID: string, toBlotterID: string) => {
      dispatch(getBlottersSliceActions().cloneState({ fromBlotterID, toBlotterID }));
    },
    [dispatch]
  );

  const clearAllBlotterState = useCallback(() => {
    dispatch(getBlottersSliceActions().clearAllBlotterState());
  }, [dispatch]);

  const contextValue = useMemo<NonNullable<ContextType<typeof BlotterTableStorageContext>>>(
    () => ({
      getState,
      setGridState,
      setFilterState,
      setExtraGridState,
      cloneState,
      clearAllBlotterState,
    }),
    [getState, setGridState, setFilterState, setExtraGridState, cloneState, clearAllBlotterState]
  );

  return <BlotterTableStorageContext.Provider value={contextValue}>{children}</BlotterTableStorageContext.Provider>;
};
